import axios from 'axios';

// FastAPI runs on port 8000 (not 5000 like Express)
const API = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'https://iassetsapi.iworkkz.com/api',
  //https://uatiassetsapi.iworkkz.com/api  http://localhost:8000/api
  headers: { 'Content-Type': 'application/json' },
  timeout: 10000,          // 10 s — prevents hanging forever
});

// ── Request: attach JWT ───────────────────────────────────────
API.interceptors.request.use(config => {
  const token = localStorage.getItem('iassets_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// ── Response: handle errors gracefully ───────────────────────
API.interceptors.response.use(
  res => res,
  err => {
    // Network down / backend unreachable — DO NOT crash, just reject
    if (!err.response) {
      console.warn('[iAssets] API unreachable:', err.message);
      return Promise.reject(err);   // caller's .catch() handles it
    }

    // 401 → force re-login (only when we have a real HTTP response)
    if (err.response.status === 401) {
      localStorage.removeItem('iassets_token');
      localStorage.removeItem('iassets_user');
      window.location.href = '/login';
    }

    return Promise.reject(err);
  }
);

// ── Auth ──────────────────────────────────────────────────────
export const authAPI = {
  login:          (data) => API.post('/auth/login', data),
  me:             ()     => API.get('/auth/me'),
  logout:         ()     => API.post('/auth/logout'),
  changePassword: (data) => API.post('/auth/change-password', data),
};

// ── Dashboard ─────────────────────────────────────────────────
export const dashboardAPI = {
  stats:          () => API.get('/dashboard/stats'),
  recentActivity: () => API.get('/dashboard/recent-activity'),
  criticalAssets: () => API.get('/dashboard/critical-assets'),
};

// ── Assets ────────────────────────────────────────────────────
export const assetsAPI = {
  list:          (params)        => API.get('/assets/', { params }),
  get:           (id)            => API.get(`/assets/${id}`),
  profile:       (id)            => API.get(`/assets/${id}/profile`),
  create:        (data)          => API.post('/assets/', data, { headers: { 'Content-Type': 'multipart/form-data' } }),
  update:        (id, d)         => API.put(`/assets/${id}`, d, { headers: { 'Content-Type': 'multipart/form-data' } }),
  delete:        (id)            => API.delete(`/assets/${id}`),
  linkSpare:     (id, spareId)   => API.post(`/assets/${id}/spares/${spareId}`),
  unlinkSpare:   (id, spareId)   => API.delete(`/assets/${id}/spares/${spareId}`),
  uploadDoc:     (id, data)      => API.post(`/assets/${id}/documents`, data, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteDoc:     (id, docId)     => API.delete(`/assets/${id}/documents/${docId}`),
  categories: ()       => API.get('/assets/meta/categories'),
  locations:  ()       => API.get('/assets/meta/locations'),
  departments:()       => API.get('/assets/meta/departments'),
  employees:  ()       => API.get('/assets/meta/employees'),
  // Subsequent costs (multiple rows per asset)
  listSubsequentCosts: (id)    => API.get(`/assets/${id}/subsequent-costs`),
  syncSubsequentCosts: (id, d) => API.post(`/assets/${id}/subsequent-costs/sync`, d),
};

// ── Work Orders ───────────────────────────────────────────────
export const workOrdersAPI = {
  list:         (params)   => API.get('/work-orders/', { params }),
  get:          (id)       => API.get(`/work-orders/${id}`),
  create:       (data)     => API.post('/work-orders/', data),
  update:       (id, data) => API.put(`/work-orders/${id}`, data),
  updateStatus: (id, data) => API.patch(`/work-orders/${id}/status`, data),
};

// ── Maintenance ───────────────────────────────────────────────
export const maintenanceAPI = {
  list:    ()     => API.get('/maintenance/'),
  overdue: ()     => API.get('/maintenance/overdue'),
  create:  (data) => API.post('/maintenance/', data),
};

// ── Compliance ────────────────────────────────────────────────
export const complianceAPI = {
  list:   ()     => API.get('/compliance/'),
  create: (data) => API.post('/compliance/', data, { headers: { 'Content-Type': 'multipart/form-data' } }),
  delete: (id)   => API.delete(`/compliance/${id}`),
};

// ── Spares ────────────────────────────────────────────────────
export const sparesAPI = {
  list:   (params) => API.get('/spares/', { params }),
  create: (data)   => API.post('/spares/', data),
  update: (id, d)  => API.put(`/spares/${id}`, d),
};

// ── Reports ───────────────────────────────────────────────────
export const reportsAPI = {
  summary: () => API.get('/reports/summary'),
};

// ── Audit ─────────────────────────────────────────────────────
export const auditAPI = {
  list: (params) => API.get('/audit/', { params }),
};

// ── Settings ──────────────────────────────────────────────────
export const settingsAPI = {
  getOrg:      ()     => API.get('/settings/org'),
  updateOrg:   (data) => API.put('/settings/org', data),
  addLocation: (data) => API.post('/settings/locations', data),
};

// ── Users ─────────────────────────────────────────────────────
export const usersAPI = {
  list:        ()       => API.get('/users/'),
  locations:   ()       => API.get('/users/locations'),
  create:      (data)   => API.post('/users/', data),
  update:      (id, d)  => API.put(`/users/${id}`, d),
  toggle:      (id)     => API.patch(`/users/${id}/toggle`),
  delete:      (id)     => API.delete(`/users/${id}`),
};

// ── User Master — Designation / Role / Succession ─────────────
export const userMastersAPI = {
  // Designations
  listDesignations:  ()     => API.get('/masters/designations'),
  createDesignation: (d)    => API.post('/masters/designations', d),
  updateDesignation: (id,d) => API.put(`/masters/designations/${id}`, d),
  deleteDesignation: (id)   => API.delete(`/masters/designations/${id}`),
  // Roles
  listRoles:   ()     => API.get('/masters/roles'),
  createRole:  (d)    => API.post('/masters/roles', d),
  updateRole:  (id,d) => API.put(`/masters/roles/${id}`, d),
  deleteRole:  (id)   => API.delete(`/masters/roles/${id}`),
  // Successions
  listSuccessions:  ()     => API.get('/masters/successions'),
  createSuccession: (d)    => API.post('/masters/successions', d),
  updateSuccession: (id,d) => API.put(`/masters/successions/${id}`, d),
  deleteSuccession: (id)   => API.delete(`/masters/successions/${id}`),
  
};

// ── Notifications ─────────────────────────────────────────────
export const notificationsAPI = {
  list:     () => API.get('/notifications/'),
  markRead: () => API.post('/notifications/mark-read'),
};


// ── Company Master ────────────────────────────────────────────
export const companyAPI = {
  // Unit / Branch
  listLocations:   (params) => API.get('/company/locations',         { params }),
  createLocation:  (data)   => API.post('/company/locations',         data),
  updateLocation:  (id, d)  => API.put(`/company/locations/${id}`,   d),
  deleteLocation:  (id)     => API.delete(`/company/locations/${id}`),

  // Department
  listDepartments:   ()     => API.get('/company/departments'),
  createDepartment:  (data) => API.post('/company/departments',         data),
  updateDepartment:  (id,d) => API.put(`/company/departments/${id}`,   d),
  deleteDepartment:  (id)   => API.delete(`/company/departments/${id}`),

  // Functional Area
  listFunctionalAreas:   ()     => API.get('/company/functional-areas'),
  createFunctionalArea:  (data) => API.post('/company/functional-areas',         data),
  updateFunctionalArea:  (id,d) => API.put(`/company/functional-areas/${id}`,   d),
  deleteFunctionalArea:  (id)   => API.delete(`/company/functional-areas/${id}`),

  // Currency
  listCurrencies:   ()     => API.get('/company/currencies'),
  createCurrency:   (data) => API.post('/company/currencies',         data),
  updateCurrency:   (id,d) => API.put(`/company/currencies/${id}`,   d),
  deleteCurrency:   (id)   => API.delete(`/company/currencies/${id}`),

  // Event Location
  listEventLocations:   ()     => API.get('/company/event-locations'),
  createEventLocation:  (data) => API.post('/company/event-locations',         data),
  updateEventLocation:  (id,d) => API.put(`/company/event-locations/${id}`,   d),
  deleteEventLocation:  (id)   => API.delete(`/company/event-locations/${id}`),

  // Financial Year
  listFinancialYears:   ()     => API.get('/company/financial-years'),
  createFinancialYear:  (data) => API.post('/company/financial-years',         data),
  updateFinancialYear:  (id,d) => API.put(`/company/financial-years/${id}`,   d),
  deleteFinancialYear:  (id)   => API.delete(`/company/financial-years/${id}`),

  // Org Levels
  listOrgLevels:   ()     => API.get('/company/org-levels'),
  createOrgLevel:  (data) => API.post('/company/org-levels',         data),
  updateOrgLevel:  (id,d) => API.put(`/company/org-levels/${id}`,   d),
  deleteOrgLevel:  (id)   => API.delete(`/company/org-levels/${id}`),

  // General Settings
  listSettings:    ()     => API.get('/company/general-settings'),
  getSettings:     ()     => API.get('/company/general-settings'),
  saveSettings:    (data) => API.put('/company/general-settings',    data),

  // Theme Style
  getTheme:        ()     => API.get('/company/theme'),
  saveTheme:       (data) => API.put('/company/theme', data),
};

// ── iSupplyLink ──────────────────────────────────────────────
export const vendorsAPI = {
  list:           (params) => API.get('/vendors/', { params }),
  get:            (id)     => API.get(`/vendors/${id}`),
  create:         (data)   => API.post('/vendors/', data),
  update:         (id, d)  => API.put(`/vendors/${id}`, d),
  delete:         (id)     => API.delete(`/vendors/${id}`),
  togglePreferred:(id)     => API.patch(`/vendors/${id}/preferred`),
};

export const supplyAPI = {
  dashboard:      ()       => API.get('/isupply/dashboard'),
  getVendor:      (id)     => API.get(`/isupply/vendors/${id}`),
  listVendors:    ()       => API.get('/isupply/vendors'),
  listPOs:        (params) => API.get('/isupply/purchase-orders', { params }),
  poLocations:    ()       => API.get('/isupply/purchase-orders/locations'),
  listInvoices:   (params) => API.get('/isupply/invoices', { params }),
  uploadInvoices: (data)   => API.post('/isupply/invoices/upload', data),
  listNotes:      (params) => API.get('/isupply/credit-notes', { params }),
  listPayments:   (params) => API.get('/isupply/payment-advices', { params }),
};

// ── Role Permissions ──────────────────────────────────────────
export const permissionsAPI = {
  /* getRoles:        ()          => API.get('/permissions/roles'),
  myPermissions:   ()          => API.get('/permissions/my'),
  updateRole:      (rid, data) => API.put(`/permissions/roles/${rid}`, data),
  getMy:         ()          => API.get('/permissions/my'),
  getForRole:    (roleId)    => API.get(`/permissions/role/${roleId}`),
  saveForRole:   (roleId, permissions) => API.post('/permissions/role', {
    role_id: roleId,
    permissions,
  }), */
  getRoles:      ()          => API.get('/permissions/roles'),
  getRolePerms:  (roleId)    => API.get(`/permissions/roles/${roleId}`),
  updateRole:    (roleId, d) => API.put(`/permissions/roles/${roleId}`, d),
  myPermissions: ()          => API.get('/permissions/my'),
};

// ── User Profile ─────────────────────────────────────────────
export const profileAPI = {
  get:            ()     => API.get('/profile'),
  update:         (data) => API.put('/profile', data),
  uploadPhoto:    (file) => {
    const fd = new FormData();
    fd.append('photo', file);
    return API.post('/profile/photo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  changePassword: (data) => API.post('/profile/change-password', data),
};

// ── Masters ────────────────────────────────────────────────────
export const mastersAPI = {
  // Category
  listCategories:  ()      => API.get('/masters/categories'),
  createCategory:  (d)     => API.post('/masters/categories', d),
  updateCategory:  (id,d)  => API.put(`/masters/categories/${id}`, d),
  deleteCategory:  (id)    => API.delete(`/masters/categories/${id}`),
  // Employee
  listEmployees:   (q)     => API.get('/masters/employees', { params: q ? {q} : {} }),
  createEmployee:  (d)     => API.post('/masters/employees', d),
  updateEmployee:  (id,d)  => API.put(`/masters/employees/${id}`, d),
  deleteEmployee:  (id)    => API.delete(`/masters/employees/${id}`),
  // Depreciation Methods
  listDepMethods:  ()      => API.get('/masters/dep-methods'),
  createDepMethod: (d)     => API.post('/masters/dep-methods', d),
  updateDepMethod: (id,d)  => API.put(`/masters/dep-methods/${id}`, d),
  deleteDepMethod: (id)    => API.delete(`/masters/dep-methods/${id}`),
  // Depreciation Types
  listDepTypes:    ()      => API.get('/masters/dep-types'),
  createDepType:   (d)     => API.post('/masters/dep-types', d),
  updateDepType:   (id,d)  => API.put(`/masters/dep-types/${id}`, d),
  deleteDepType:   (id)    => API.delete(`/masters/dep-types/${id}`),
  // Location Master (locations table used by assets)
  listLocations:   ()      => API.get('/masters/locations'),
  createLocation:  (d)     => API.post('/masters/locations', d),
  updateLocation:  (id,d)  => API.put(`/masters/locations/${id}`, d),
  deleteLocation:  (id)    => API.delete(`/masters/locations/${id}`),
  // Asset Class Master
  listAssetClasses:  ()      => API.get('/masters/asset-classes'),
  createAssetClass:  (d)     => API.post('/masters/asset-classes', d),
  updateAssetClass:  (id,d)  => API.put(`/masters/asset-classes/${id}`, d),
  deleteAssetClass:  (id)    => API.delete(`/masters/asset-classes/${id}`),
  // Warranty Period Master
  listWarrantyPeriods:  ()     => API.get('/masters/warranty-periods'),
  createWarrantyPeriod: (d)    => API.post('/masters/warranty-periods', d),
  updateWarrantyPeriod: (id,d) => API.put(`/masters/warranty-periods/${id}`, d),
  deleteWarrantyPeriod: (id)   => API.delete(`/masters/warranty-periods/${id}`),
  // Asset Financial Values (per asset per financial year)
  listFinancialYears:   ()      => API.get('/masters/financial-years'),
  createFinancialYear:  (d)     => API.post('/masters/financial-years', d),
  updateFinancialYear:  (id, d) => API.put(`/masters/financial-years/${id}`, d),
  deleteFinancialYear:  (id)    => API.delete(`/masters/financial-years/${id}`),
  
  listFinancialValues:  (assetId)    => API.get(`/masters/financial-values/${assetId}`),
  upsertFinancialValues:(assetId, d) => API.post(`/masters/financial-values/${assetId}`, d),
  // Lookups for Employee Master dropdowns
  lookupDesignations: () => API.get('/masters/lookup/designations-list'),
  lookupDepartments:  () => API.get('/masters/lookup/departments'),

  deleteDepType:   (id)      => API.delete(`/masters/dep-types/${id}`),

  // Employee Login & Permissions
  getEmployeeLoginStatus: (id)    => API.get(`/employees/${id}/login-status`),
  setEmployeePassword:    (id, d) => API.post(`/employees/${id}/set-password`, d),
  updateEmployeeRole:     (id, d) => API.put(`/employees/${id}/update-role`, d),
  removeEmployeeLogin:    (id)    => API.delete(`/employees/${id}/remove-login`),

   // Maintenance Type Master
   listMaintenanceTypes:   ()      => API.get('/masters/maintenance-types'),
   createMaintenanceType:  (d)     => API.post('/masters/maintenance-types', d),
   updateMaintenanceType:  (id, d) => API.put(`/masters/maintenance-types/${id}`, d),
   deleteMaintenanceType:  (id)    => API.delete(`/masters/maintenance-types/${id}`),
 
   // Frequency Master
   listFrequencies:        ()      => API.get('/masters/frequencies'),
   createFrequency:        (d)     => API.post('/masters/frequencies', d),
   updateFrequency:        (id, d) => API.put(`/masters/frequencies/${id}`, d),
   deleteFrequency:        (id)    => API.delete(`/masters/frequencies/${id}`),

   // Failure Type Master
   listFailureTypes:   ()      => API.get('/masters/failure-types'),
   createFailureType:  (d)     => API.post('/masters/failure-types', d),
   updateFailureType:  (id, d) => API.put(`/masters/failure-types/${id}`, d),
   deleteFailureType:  (id)    => API.delete(`/masters/failure-types/${id}`),

};
 export const emailConfigAPI = {
  get:  ()          => API.get('/email-config'),
  save: (d)         => API.post('/email-config', d),
  test: (toEmail)   => API.post('/email-config/test', { to_email: toEmail }),
};

export const tasksAPI = {
  list:          (params) => API.get('/tasks/', { params }),
  updateStatus:  (id, status) => API.patch(`/tasks/${id}/status`, { status }),
  sendNotification: (id)  => API.post(`/notifications/send/${id}`),
  logs:          ()       => API.get('/notifications/logs'),
};

export default API;
